import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { DeeplinkAction } from "@/shared/config/deeplink";

/**
 * 알림톡 딥링크(`?deeplink=exam_open` 등)로 진입한 "대기 중인 액션" — persist(sessionStorage).
 *
 * `examEntryStore`(transient, 더보기 전용)와 관심사가 다르다:
 * - 이 스토어는 콜드부트/로그인 왕복(AuthGuard 리다이렉트)을 넘어 생존해야 한다
 *   (미인증 상태로 딥링크 진입 → 로그인 완료 → `/main` 착지까지 의도를 보존).
 * - `examEntryStore` 는 반대로 persist 금지가 설계 의도(새로고침 시 오래된 요청 재생 방지)다.
 * 두 스토어는 TabShell 의 `openExamSheet()` 단일 종착점을 공유할 뿐, 하나로 합치지 않는다.
 *
 * 별도 name 키(`deeplink-intent`)를 쓰므로 `clearAllTokens`/`logOut` 이 지우지 않는다(자동 격리).
 */
type DeeplinkState = {
  pendingAction: DeeplinkAction | null;
  setPendingAction: (action: DeeplinkAction) => void;
  clearPendingAction: () => void;
  /**
   * 초대 링크에 실려 온 비식별 공유 코드(검증 완료된 base62 12자 — SHARE_CODE_QUERY_PARAM).
   * pendingAction 과 같은 수명(콜드부트·로그인 왕복 생존)이어야 등록 화면 도착 시점에
   * 살아 있다. 등록 화면이 1회 소비해 자동 preview → shareCode 등록 경로를 연다.
   */
  pendingShareCode: string | null;
  setPendingShareCode: (shareCode: string) => void;
  clearPendingShareCode: () => void;
};

export const useDeeplinkStore = create<DeeplinkState>()(
  persist(
    (set) => ({
      pendingAction: null,
      setPendingAction: (action) => set({ pendingAction: action }),
      clearPendingAction: () => set({ pendingAction: null }),
      pendingShareCode: null,
      setPendingShareCode: (shareCode) => set({ pendingShareCode: shareCode }),
      clearPendingShareCode: () => set({ pendingShareCode: null }),
    }),
    {
      name: "deeplink-intent",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * React hook 컨텍스트 없이 (전역 캡처 인터셉터 등에서) 직접 호출할 수 있는 단독 함수.
 */
export const getPendingDeeplink = (): DeeplinkAction | null =>
  useDeeplinkStore.getState().pendingAction;

export const setPendingDeeplink = (action: DeeplinkAction): void =>
  useDeeplinkStore.getState().setPendingAction(action);

export const clearPendingDeeplink = (): void =>
  useDeeplinkStore.getState().clearPendingAction();

export const getPendingShareCode = (): string | null =>
  useDeeplinkStore.getState().pendingShareCode;

export const setPendingShareCode = (shareCode: string): void =>
  useDeeplinkStore.getState().setPendingShareCode(shareCode);

export const clearPendingShareCode = (): void =>
  useDeeplinkStore.getState().clearPendingShareCode();
